import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, advertisersAPI } from './api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [advertiser, setAdvertiser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to fetch advertiser data
  const fetchAdvertiser = async (advertiserId) => {
    if (!advertiserId) {
      setAdvertiser(null);
      return;
    }
    try {
      const response = await advertisersAPI.getById(advertiserId);
      setAdvertiser(response.data);
      localStorage.setItem('advertiser', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to fetch advertiser:', error);
      setAdvertiser(null);
    }
  };

  // Function to refresh advertiser data (called after logo update)
  const refreshAdvertiser = async () => {
    if (user?.advertiser_id) {
      await fetchAdvertiser(user.advertiser_id);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const storedAdvertiser = localStorage.getItem('advertiser');
      
      if (token && storedUser) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
          
          // Fetch fresh advertiser data if user has advertiser_id
          if (response.data.advertiser_id) {
            await fetchAdvertiser(response.data.advertiser_id);
          } else if (storedAdvertiser) {
            localStorage.removeItem('advertiser');
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('advertiser');
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { access_token, user: userData, advertiser: advertiserData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Store advertiser info if present
    if (advertiserData) {
      setAdvertiser(advertiserData);
      localStorage.setItem('advertiser', JSON.stringify(advertiserData));
    } else {
      setAdvertiser(null);
      localStorage.removeItem('advertiser');
    }
    
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('advertiser');
    setUser(null);
    setAdvertiser(null);
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isAdmin = () => user?.role === 'admin';
  const isUser = () => user?.role === 'user';
  const isAdvertiser = () => user?.role === 'advertiser';
  const canManageUsers = () => isSuperAdmin() || isAdmin();
  const canManageAdmins = () => isSuperAdmin();
  const canManageCampaigns = () => isSuperAdmin() || isAdmin();

  return (
    <AuthContext.Provider value={{
      user,
      advertiser,
      loading,
      login,
      register,
      logout,
      refreshAdvertiser,
      isSuperAdmin,
      isAdmin,
      isUser,
      isAdvertiser,
      canManageUsers,
      canManageAdmins,
      canManageCampaigns,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
